import moment from 'moment';
import format from 'string-template';

import { DateFormat } from '../../../utils/date';
import { getCurrencySymbol } from '../../../utils/formatters';
import { DURATION_UNIT, toMomentUnit } from '../../../utils/durationUnit';
import { getUpfrontPayment } from '../challengePricing';
import texts from './termClauses.json';

const FEBRUARY_DAYS = 28;
const LAST_DAY = 31;
const EMPTY_STRING = '______';

const ContractType = {
  CANCEL_AT_PERIOD_END: 'cancelAtPeriodEnd',
  WITH_REMAINING_PAYMENTS: 'withRemainingPayments',
  WITHOUT_REMAINING_PAYMENTS: 'withoutRemainingPayments',
  SEQUENTIAL: 'sequential',
  ADVANCE_PAYMENT: 'advancePayment',
  WITH_POST_COMMITMENT_RATES: 'withPostCommitmentRates',
  ADVANCE_PAYMENT_WITH_POST_COMMITMENT_RATES: 'advancePaymentWithPostCommitmentRates',
  CHALLENGE: 'challenge',
};

const generateTerms = (contract, isAmendment = false) => {
  // Return an empty array if there is no contract to generate the terms for.
  if (!contract) {
    return [];
  }

  const {
    startDate,
    initialPayment,
    initialTerm,
    minSubscriptionMonths,
    monthlyPayment,
    currency,
    cancelAtPeriodEnd,
    recurringBillingMonths = 1,
    version,
    postCommitmentPayment,
    challengeId = null,
    durationUnit = DURATION_UNIT.MONTH,
  } = contract;

  /*
    The unit `initialTerm` and `minSubscriptionMonths` are counted in. Only
    challenge contracts can carry 'week' — every other clause set describes a
    monthly subscription, so this stays 'months' for them by default.
  */
  const termMomentUnit = toMomentUnit(durationUnit);

  // Calculate the payment day copy
  const startDay = moment(startDate).date();
  let paymentDay;
  switch (startDay) {
    case LAST_DAY:
      paymentDay = texts.lastDay;
      break;
    default:
      paymentDay = startDate?.isValid() ? moment(startDate.toISOString()).format('Do') : '';
      break;
  }

  const exception = (startDay !== LAST_DAY && startDay > FEBRUARY_DAYS) ? texts.exception : '';

  const remainingMonths = minSubscriptionMonths - initialTerm;

  // Calculate the remaining payments
  const remainingCommitment = remainingMonths / initialTerm;

  // Calculate the payment cycle
  const paymentCycle = recurringBillingMonths === 1
    ? texts.eachMonth
    : format(texts.everyXMonths, { recurringBillingMonths });

  // Subscription cycle after the commitment finished
  const subscriptionCycle = recurringBillingMonths === 1
    ? texts.monthly
    : format(texts.everyXMonths, { recurringBillingMonths });

  /*
    Resolved rather than read straight off the contract: a challenge is billed once at checkout and
    carries no advance, so an advance stored before that rule existed must not reach the clauses.
    Taking it from the same helper the stored price uses keeps the two describing one contract.
  */
  const upfrontPayment = getUpfrontPayment(contract);

  // Calculate the initial payment
  const upfrontPaymentValue = parseFloat(upfrontPayment);
  const initialPaymentValue = parseFloat(initialPayment);
  const computedInitialPaymentValue = upfrontPaymentValue > 0
    ? initialPaymentValue - upfrontPaymentValue
    : initialPaymentValue;

  const templateData = {
    startDate: startDate?.isValid() ? startDate.format(DateFormat.CONTRACT_DATE_FORMAT) : '',
    advancePaymentDate: moment().format(DateFormat.CONTRACT_DATE_FORMAT),
    currency: getCurrencySymbol(currency),
    initialPayment: !Number.isNaN(computedInitialPaymentValue) ? computedInitialPaymentValue.toFixed(2) : EMPTY_STRING,
    initialTerm,
    monthlyPayment: monthlyPayment || EMPTY_STRING,
    minSubscriptionMonths: minSubscriptionMonths || EMPTY_STRING,
    remainingCommitment: remainingCommitment || EMPTY_STRING,
    nextPaymentDate: startDate?.isValid()
      ? startDate?.clone().add(initialTerm, termMomentUnit).format(DateFormat.CONTRACT_DATE_FORMAT) : '',
    paymentDay,
    exception,
    paymentCycle,
    subscriptionCycle,
    endOfCommitmentDate: startDate?.isValid()
      ? startDate.clone().add(minSubscriptionMonths, termMomentUnit).format(DateFormat.CONTRACT_DATE_FORMAT) : '',
    /*
      Additive: month contracts (durationUnit absent/'month') render "month(s)"
      exactly as before; week challenges render "week(s)". Matches the
      launchpad's contract-terms.ts so both generators emit identical text.
    */
    termUnit: durationUnit === DURATION_UNIT.WEEK ? 'week(s)' : 'month(s)',
    upfrontPayment,
    remainingMonths,
    postCommitmentPayment,
  };

  let clausesKey;
  if (version === 1) {
    if (cancelAtPeriodEnd) {
      clausesKey = ContractType.CANCEL_AT_PERIOD_END;
    } else if (Number(initialTerm) === Number(minSubscriptionMonths)) {
      clausesKey = ContractType.WITHOUT_REMAINING_PAYMENTS;
    } else {
      clausesKey = ContractType.WITH_REMAINING_PAYMENTS;
    }
  } else if (version === 2) {
    /*
      - If both an upfront payment and post commitment payment are configured, use the combined clause type.
      - If only a post-commitment payment is configured, use the post commitment clause type.
      - If only an upfront payment is configured, use the advance payment clause type.
      - Otherwise, use the sequential clauses, which have a PIF period and a monthly payment period.
    */
    const hasAdvancePayment = upfrontPaymentValue > 0;

    /*
      A challenge is billed once at checkout, so `getUpfrontPayment` reports no advance for one and
      the split clauses below cannot describe it. The one-time clause is also the only challenge
      clause the server and the launchpad render, so all three describe the same contract alike.
    */
    if (challengeId) {
      clausesKey = ContractType.CHALLENGE;
    } else if (postCommitmentPayment && hasAdvancePayment) {
      clausesKey = ContractType.ADVANCE_PAYMENT_WITH_POST_COMMITMENT_RATES;
    } else if (postCommitmentPayment) {
      clausesKey = ContractType.WITH_POST_COMMITMENT_RATES;
    } else if (hasAdvancePayment) {
      clausesKey = ContractType.ADVANCE_PAYMENT;
    } else {
      clausesKey = ContractType.SEQUENTIAL;
    }
  }

  // Get the version key to get the clauses copy.
  const versionKey = `version${version}`;

  // Get the base contract clauses
  const contractClauses = texts.contractClauses[versionKey][clausesKey].map((clause) => format(clause, templateData));

  // Add amendment clauses if this is an amendment
  const amendmentFlag = contract.isAmendment ? contract.isAmendment : isAmendment;
  if (amendmentFlag) {
    contractClauses.push(texts.amendmentClause);
  }
  return contractClauses;
};

export {
  generateTerms,
};
